import React, {ReactElement} from "react";
import {
  getCountryCallingCode,
  getCountries,
  parsePhoneNumber,
  Country,
} from "react-phone-number-input";
import {default as PhoneInput} from "react-phone-number-input/input";
import countries from "react-phone-number-input/locale/en.json";

import GDPRContext from "/src/contexts/GDPRContext";
import Input from "../input/Input";
import ErrorMessage from "../error-message/ErrorMessage";
import Dropdown from "../dropdown/Dropdown";

import translate from "/src/utils/translate";
import {css} from "/src/utils/utils";

import {BaseInput} from "/src/types";

import * as styles from "./phone.module.scss";

interface PhoneProps extends BaseInput {
  placeholder?: string;
  onChange?: (str: string) => unknown;
  transparentBg?: boolean;
}

interface PhoneInputProps extends PhoneProps {
  type: string;
  autocomplete: string;
  value: string;
  onChange: (str: string) => unknown;
  onKeyDown: (str: string) => unknown;
}

// helper method to get list of countries options
const countriesOptions = getCountries()
  .map(country => {
    const code = `+${getCountryCallingCode(country)}`;
    return {
      selectedLabel: code,
      label: `${countries[country]} (${code})`,
      value: country,
    };
  })
  .sort((a, b) => a.label.localeCompare(b.label));

// this is to be used as custom input in react-phone-number-input/input component
// ref: https://catamphetamine.gitlab.io/react-phone-number-input/#without-country-select
const CustomInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (phoneProps, ref) => {
    const {
      required = false,
      name,
      value = "",
      placeholder = "",
      status,
      disabled = false,
      readOnly = false,
      onChange,
      darkMode = false,
      transparentBg = false,
      label = translate("Phone"),
    } = phoneProps;

    return (
      <Input
        key={`${name}-phone-input-wrapper`}
        onChange={onChange}
        value={value}
        inputRef={ref}
        darkMode={darkMode}
        placeholder={placeholder}
        label={label}
        type="tel"
        name={`${name}-phone-input`}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        status={status}
        transparentBg={transparentBg}
        disableError
      />
    );
  }
);

CustomInput.displayName = "CustomInput";

const Phone = (props: PhoneProps): ReactElement => {
  const {
    required = false,
    name,
    value = "",
    placeholder = "",
    errorMessage = "",
    status,
    disabled = false,
    readOnly = false,
    onChange,
    darkMode = false,
    transparentBg = false,
    label = translate("Phone"),
  } = props;

  const inputWrapperRef = React.useRef<HTMLDivElement | null>(null);

  const {countryCode} = React.useContext(GDPRContext);

  const [phoneNo, setPhoneNo] = React.useState<string>("");
  const [countryCodeValue, setCountryCodeValue] = React.useState<string>("");
  const [initialCountryCodeValue, setInitialCountryCodeValue] =
    React.useState<string>("");

  const pickCountry = React.useCallback(() => {
    if (countryCode != null) {
      return countryCode;
    }

    return "";
  }, [countryCode]);

  React.useEffect(() => {
    if (initialCountryCodeValue !== "") {
      return;
    }

    // if there is initial phone value, we need to grab the country code
    const destructureInitialValue =
      value != null ? parsePhoneNumber(value) : null;

    setPhoneNo(
      destructureInitialValue != null
        ? destructureInitialValue.nationalNumber
        : ""
    );

    let initialCountryCode: string =
      destructureInitialValue != null ? destructureInitialValue.country! : "";

    if (initialCountryCode === "") {
      initialCountryCode = pickCountry();
    }

    setInitialCountryCodeValue(initialCountryCode);
  }, [value, pickCountry, initialCountryCodeValue]);

  React.useEffect(() => {
    setCountryCodeValue(initialCountryCodeValue || "US");
  }, [initialCountryCodeValue]);

  const isInvalid = status === "error";

  const onPhoneInputChange = (val: string) => {
    setPhoneNo(val);

    if (onChange != null) {
      onChange(val || "");
    }
  };

  const onCountryCodeChange = (val: string) => {
    if (val !== countryCodeValue) {
      // we need to update phoneNo value

      const code = val ? `+${getCountryCallingCode(val as Country)}` : "";
      const destructureInitialValue =
        value != null ? parsePhoneNumber(phoneNo) : null;
      const temp = `${code}${
        destructureInitialValue != null
          ? destructureInitialValue.nationalNumber
          : phoneNo
      }`;
      setPhoneNo(temp);
    }

    // we need state change so that the format get's updated properly
    setCountryCodeValue(val);
  };

  return (
    <div
      ref={inputWrapperRef}
      className={css(
        styles.phone,
        darkMode ? styles.phoneDark : "",
        transparentBg ? styles.phoneTransparent : ""
      )}
    >
      <input type="hidden" name={name} value={phoneNo || ""} />
      <input type="hidden" name="country_code" value={countryCodeValue || ""} />
      <div className={css(styles.phone__el)}>
        <Dropdown
          key={`${name}-phone-input-country`}
          darkMode={darkMode}
          value={countryCodeValue || ""}
          placeholder={translate("Select country")}
          options={[...countriesOptions]}
          onChange={onCountryCodeChange}
          name={`${name}-country-code`}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          label={translate("Country code")}
          status={status}
          transparentBg={transparentBg}
          disableError
        />
        <PhoneInput
          key={`${name}-phone-input-wrapper`}
          country={(countryCodeValue || "") as Country}
          value={phoneNo || ""}
          onChange={onPhoneInputChange}
          darkMode={darkMode}
          placeholder={placeholder}
          label={label}
          type="tel"
          name={`${name}-phone-input`}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          status={status}
          transparentBg={transparentBg}
          inputComponent={CustomInput as any}
        />
      </div>
      <ErrorMessage message={errorMessage} visible={isInvalid} />
    </div>
  );
};

export default Phone;
